import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Lock, Key, Database, 
  RefreshCw, Smartphone, Wallet, Eye, Server, FileKey
} from 'lucide-react';

const AuraShieldPage = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-20">
        {/* Back Button */}
        <Link
          to="/self-phone"
          className="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to SELF Phone
        </Link>

        {/* Hero Section */}
        <motion.section 
          className="container mx-auto px-4 py-16 text-center"
          initial="initial"
          animate="animate"
          variants={fadeIn}
        >
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Aura Shield Protection
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Your impenetrable defense against digital intrusion. Aura Shield creates a sovereign space for your data, 
            ensuring your digital sovereignty through advanced encryption and privacy controls.
          </p>
        </motion.section>

        {/* Core Features Grid */}
        <section className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Lock className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Zero-Knowledge Encryption</h3>
              <p className="text-gray-400">
                Your data is encrypted before it leaves your device, ensuring that only you have access to your information.
                Not even we can read your protected data.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Key className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Private Key Control</h3>
              <p className="text-gray-400">
                You maintain complete control of your encryption keys. Your private keys never leave your device,
                ensuring true data sovereignty.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Database className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Secure Data Vault</h3>
              <p className="text-gray-400">
                Your encrypted data is stored in a secure vault with multiple layers of protection,
                making unauthorized access virtually impossible.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Server className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Distributed Architecture</h3>
              <p className="text-gray-400">
                Your protected data is distributed across multiple secure nodes, eliminating single points of failure
                and ensuring high availability.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Eye className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Privacy Controls</h3>
              <p className="text-gray-400">
                Granular control over what data is shared and with whom. You decide exactly what information
                is visible to others.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Wallet className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Private Earnings</h3>
              <p className="text-gray-400">
                Securely earn ESSENCE through your protected data and AI interactions. Your earnings are private and controlled only by you.
                <Link to="/signal-engine" className="block mt-2 text-indigo-400 hover:text-indigo-300 transition-colors">
                  Learn more about Signal Engine →
                </Link>
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-900 p-6 rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <FileKey className="w-10 h-10 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Quantum-Ready Security</h3>
              <p className="text-gray-400">
                Future-proof encryption algorithms designed to withstand advances in quantum computing,
                protecting your data for years to come.
              </p>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="container mx-auto px-4 py-16">
          <motion.div 
            className="bg-gray-900 p-8 rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <h2 className="text-3xl font-bold mb-6">How Aura Shield Works</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                Aura Shield provides continuous protection through multiple layers of security:
              </p>
              <ol className="list-decimal list-inside space-y-3 pl-4">
                <li>Data is encrypted on your device using military-grade encryption algorithms</li>
                <li>Encrypted data is split into fragments and distributed across secure nodes</li>
                <li>Access is controlled through your private keys, which never leave your device</li>
                <li>All operations are performed using zero-knowledge proofs for maximum privacy</li>
                <li>Real-time monitoring detects and blocks unauthorized access attempts</li>
              </ol>
            </div>
          </motion.div>
        </section>

        {/* Powered By Section */}
        <section className="container mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <p className="text-gray-400">
              Powered by{' '}
              <a 
                href="https://purecipher.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Pure Cipher's
              </a>
              {' '}OMNISEAL™ encryption suite
            </p>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default AuraShieldPage; 